import { HOURS_PER_NINKU, ROLE_AGENT, ROLE_MANAGER } from '../domain/constants';
import type { Dest, Staff, TaskGroupKey, TaskRow } from '../domain/types';
import { sum } from '../lib/safeMath';
import { allocationsAt } from './capacity';
import { destOf } from './taskDest';

export type SimulationParams = {
  tasks: readonly TaskRow[];
  group: TaskGroupKey;
  /** overrideKey(group, taskId) → 担い手 */
  overrides: Readonly<Record<string, Dest>>;
  /** 移管先で同じ作業にかかる時間(現場=100%)。 */
  effA: number;
  effB: number;
  effC: number;
  /** 外注単価(円/h) */
  rate: number;
  /** 対象の工数(人工) */
  agents: number;
};

export type SimulationResult = {
  byDest: Readonly<Record<Dest, number>>;
  rawByDest: Readonly<Record<Dest, number>>;
  freedHours: number;
  totalHours: number;
  /** 対象G全体の創出余力(人工) */
  savedNinku: number;
  /** 受け皿側(Kセンター + BO)に必要な体制(人工) */
  receiverNinku: number;
  outsourceCostYen: number;
};

export const overrideKey = (group: TaskGroupKey, taskId: string): string => `${group}:${taskId}`;

export const simDestOf = (
  task: TaskRow,
  group: TaskGroupKey,
  overrides: Readonly<Record<string, Dest>>,
): Dest => overrides[overrideKey(group, task.id)] ?? destOf(task, group);

export const simulate = (params: SimulationParams): SimulationResult => {
  const eff: Readonly<Record<Dest, number>> = {
    G: 1,
    A: params.effA / 100,
    B: params.effB / 100,
    C: params.effC / 100,
  };
  const by: Record<Dest, number> = { G: 0, A: 0, B: 0, C: 0 };
  const raw: Record<Dest, number> = { G: 0, A: 0, B: 0, C: 0 };
  params.tasks.forEach((task) => {
    const dest = simDestOf(task, params.group, params.overrides);
    const hours = task.hoursPerMonth ?? 0;
    raw[dest] += hours;
    by[dest] += hours * eff[dest];
  });
  // 現場に残す工数が 160h を超えても、創出余力はマイナスにしない
  const freedHours = Math.max(0, HOURS_PER_NINKU - by.G);
  return {
    byDest: by,
    rawByDest: raw,
    freedHours,
    totalHours: by.G + by.A + by.B + by.C,
    savedNinku: (freedHours * params.agents) / HOURS_PER_NINKU,
    receiverNinku: ((by.A + by.B) * params.agents) / HOURS_PER_NINKU,
    outsourceCostYen: by.C * params.rate * params.agents,
  };
};

const countsAsAgentWork = (role: string | null): boolean =>
  role === ROLE_AGENT || role === ROLE_MANAGER || (role?.startsWith('応援') ?? false);

/** 10月時点で、代理人・施工管理として使っている工数(人工、四捨五入)。 */
export const defaultAgentNinku = (staff: readonly Staff[], group: TaskGroupKey): number =>
  Math.round(
    sum(
      staff
        .filter((s) => s.group === group)
        .flatMap((s) => allocationsAt(s, 0).filter((a) => countsAsAgentWork(a.role)))
        .map((a) => a.percent),
    ) / 100,
  );
